import { zodResolver } from "@hookform/resolvers/zod";
import {
  KdsCardFile,
  KdsDropfile,
  KdsModal,
  KdsText,
  KdsTextField,
} from "@kazaar-platform/design-system";
import { RiCheckboxCircleLine } from "@remixicon/react";
import { useIsMutating } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { isAxiosError } from "axios";
import { useAtomValue } from "jotai";
import { sumBy } from "lodash";
import { useContext, useMemo, useState } from "react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import {
  FileContext,
  FileContextProvider,
} from "@/components/FileUploadAsync/FileUploadAsync.context";
import { type OrderItemDetailsSandboxItemSandboxItemVersionFileReadDto } from "@/config/apiClient";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { formatQuantity } from "@/helpers/formatQuantity";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { useOrderItems, useSandboxItems, useToasts } from "@/hooks";
import { useDirectUpload } from "@/hooks/useDirectUpload";
import { userAtom } from "@/store";
import {
  type InferredModalGraphicSchema,
  modalGraphicSchema,
} from "./ModalHDF.schema";
import {
  type ModalHDFConnectedProps,
  type ModalHDFProps,
  type ModalHDFVersionProps,
} from "./ModalHDF.types";

// Connected Modal is an "usable everywhere" modal when you're missing some details to invoke the Modal
const ModalHDFConnected = ({
  orderItemId,
  orderId,
  ...rest
}: ModalHDFConnectedProps) => {
  const { useGetOrderItem } = useOrderItems();
  const { data, isPending } = useGetOrderItem({ orderItemId, orderId });

  if (isPending || !data?.data) {
    return null;
  }

  const orderItem = data.data.orderItem;

  return <ModalHDF {...rest} orderItem={orderItem} key={orderItem.id} />;
};

// Component for Version Block
const ModalHDFVersion = ({
  methods,
  index,
  versionFile,
}: ModalHDFVersionProps) => {
  const { t } = useTranslate();

  const [isLoading, setIsLoading] = useState(false);

  const { pushFile, setFileUploadState } = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const handleUpload = async (file: File) => {
    try {
      const uploadDetails = await createFileUploadUrl(
        "orderItem.deliveryNotes",
        file
      );
      const newSignedId = uploadDetails?.fileSignedId;

      pushFile(newSignedId, {
        fileName: file.name,
        size: file.size,
        contentType: file.type,
      });

      await uploadFile(file, uploadDetails);

      setFileUploadState(newSignedId, "uploaded");

      methods.setValue(`versions.${index}.fileSignedId`, newSignedId);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 422) {
          setFileUploadState(error.response?.data.fileSignedId, "error");
          methods.setError(`versions.${index}.fileSignedId`, {
            type: "manual",
            message: t("dropfile.errorDropfileType"),
          });
          methods.setValue(`versions.${index}.fileSignedId`, undefined);
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded border-[1px] border-slate-200 bg-white p-4">
      <div className="flex w-full flex-row flex-wrap items-center gap-4 md:flex-nowrap">
        <div className="w-full md:w-3/4">
          <KdsTextField
            placeholder={t("inputs.version.placeholder")}
            className="text-sm"
            disabled
            hasError={
              !!methods.formState.errors.versions &&
              !!methods.formState.errors.versions[index]?.name
            }
            {...methods.register(`versions.${index}.name`, { required: true })}
          />
        </div>
        <div className="w-full md:w-1/4">
          <KdsTextField
            type="number"
            disabled
            className="text-sm"
            {...methods.register(`versions.${index}.quantity`, {
              valueAsNumber: true,
              required: true,
            })}
          />
        </div>
      </div>

      {versionFile ? (
        <KdsCardFile
          fileName={versionFile.fileName}
          fileUrl={versionFile.url}
          fileSize={versionFile.size}
          isViewable
        />
      ) : (
        <Controller
          name={`versions.${index}.fileSignedId`}
          render={({ field, fieldState }) => (
            <KdsDropfile
              dataCy="fileUploadForDesign"
              errorMessage={getDropfileErrorMessage(fieldState.error?.message)}
              hasError={!!fieldState.error}
              isDeletable={!!field.value}
              isReadable={!!field.value}
              isLoading={isLoading}
              maxSize={SIZE_5G}
              onDelete={() => field.onChange(undefined)}
              onUpload={(file) => void handleUpload(file)}
              onUploadError={() =>
                methods.setValue(`versions.${index}.fileSignedId`, undefined)
              }
              onUploadStart={() => {
                setIsLoading(true);
                methods.clearErrors(`versions.${index}.fileSignedId`);
              }}
              onUploadSuccess={() => setIsLoading(false)}
              placeholderDescription={t("dropfile.placeholderMaxSize5GB")}
              placeholderTitle={t("dropfile.placeholderTitle")}
            />
          )}
        />
      )}
    </div>
  );
};

export const ModalHDF = ({
  orderItem,
  isOpen,
  onOpenChange,
  onError,
  addToast,
}: ModalHDFProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const userAtomValue = useAtomValue(userAtom);

  const preferredAddToast = addToast || add;

  const { usePatchSandboxItem } = useSandboxItems();
  const { mutateAsync: patchSandboxItem } = usePatchSandboxItem();

  // Define Total Quantity
  const totalQuantity = sumBy(
    orderItem.sandboxItem.SandboxItemVersion,
    (version) => sumBy(version.SandboxItemVersionDispatch, "quantity")
  );

  const formattedTotalQuantity = useMemo(
    () =>
      formatQuantity({ quantity: totalQuantity, locale: userAtomValue?.lang }),
    [totalQuantity, userAtomValue?.lang]
  );

  // Define Form
  const methods = useForm<InferredModalGraphicSchema>({
    criteriaMode: "all",
    mode: "all",
    reValidateMode: "onChange",
    resolver: zodResolver(modalGraphicSchema),
    defaultValues: {
      versions: orderItem.sandboxItem.SandboxItemVersion?.map((version) => ({
        versionId: version.id,
        name: version.name,
        quantity: sumBy(version.SandboxItemVersionDispatch, "quantity"),
        fileSignedId: "",
      })),
    },
  });

  // Define isFetching based on mutationKey
  const isFetching = useIsMutating({ mutationKey: ["patchSandboxItem"] });

  // Handle OnSubmit
  const onSubmit: SubmitHandler<InferredModalGraphicSchema> = async (body) => {
    const res = await patchSandboxItem({
      orderId: orderItem.orderId,
      orderItemId: orderItem.id,
      sandboxItemId: orderItem.sandboxItemId,
      sandboxItemUpdateCommand: {
        sandboxItemVersion: body.versions.map((v) => ({
          id: v.versionId,
          fileSignedId: v.fileSignedId,
        })),
      },
    });

    if (res.status === 200) {
      preferredAddToast({
        state: "success",
        title: t("cardOrder.toast.successAddGraphism"),
      });
    } else {
      if (onError) {
        onError();
      }
      preferredAddToast({
        state: "error",
        title: t("toast.generic.error"),
      });
    }

    onOpenChange(false);
  };

  const onSubmitError = () => {
    preferredAddToast({
      state: "error",
      title: t("toast.generic.error"),
    });
  };

  return (
    <FileContextProvider>
      <FileContext.Consumer>
        {(ctx) => (
          <KdsModal
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            dialogProps={{
              dialogHeaderProps: {
                title: t("cardOrder.modalGraphic.title"),
              },
              content: (
                <FormProvider {...methods}>
                  <div className="mt-4 flex items-center justify-between">
                    <KdsText>
                      {t(
                        "order.catalogMediumConfiguration.stepVersions.versionManagement"
                      )}
                    </KdsText>

                    <div className="flex w-fit flex-wrap items-center gap-2">
                      <KdsText>
                        {t("cardOrder.modalGraphic.totalQuantity", {
                          total: `${formattedTotalQuantity} / ${formattedTotalQuantity}`,
                        })}
                      </KdsText>
                      <RiCheckboxCircleLine className="h-5 w-5 text-emerald-500" />
                    </div>
                  </div>

                  <div className="flex max-h-[50vh] flex-col gap-4 overflow-y-auto">
                    {methods.getValues("versions").map((version, index) => (
                      <ModalHDF.Version
                        key={version.versionId}
                        methods={methods}
                        versionFile={
                          orderItem.sandboxItem.SandboxItemVersion[index]
                            ?.file as OrderItemDetailsSandboxItemSandboxItemVersionFileReadDto
                        }
                        index={index}
                        addToast={preferredAddToast}
                      />
                    ))}
                  </div>
                </FormProvider>
              ),
              dialogFooterProps: {
                buttonProps: [
                  {
                    children: t("buttons.cancel"),
                    variant: "ghost",
                    color: "neutral",
                    isLoading: methods.formState.isSubmitting || !!isFetching,
                    disabled:
                      methods.formState.isSubmitting ||
                      !!isFetching ||
                      ctx.isUploading,
                    onPress: () => onOpenChange(false),
                  },
                  {
                    children: t("buttons.validateVersions"),
                    type: "submit",
                    isLoading: methods.formState.isSubmitting || !!isFetching,
                    disabled:
                      methods.formState.isSubmitting ||
                      !!isFetching ||
                      ctx.isUploading,
                    dataCy: "submitDesignButton",
                    onPress: () =>
                      void methods.handleSubmit(onSubmit, onSubmitError)(),
                  },
                ],
              },
            }}
          />
        )}
      </FileContext.Consumer>
    </FileContextProvider>
  );
};

ModalHDF.Version = ModalHDFVersion;
ModalHDF.Connected = ModalHDFConnected;
